/**
 * Studio — Process Template Editor (3 panneaux: Steps | Activities | Config).
 */

import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import {
  createProcessTemplateStudio,
  getProcessTemplateFullStudio,
  updateProcessTemplateStudio,
  publishProcessTemplateStudio,
  studioUpdateStep,
  studioUpdateActivity,
} from "../../../api/opsV2/client";
import { getCurrentOrgId } from "../../../state/orgContext";
import type {
  ProcessTemplateFullDetailStudio,
  ProcessStepTemplateStudioOut,
  ProcessActivityTemplateStudioOut,
} from "../../../api/opsV2/types";
import { StepsPanel } from "../../components/studio/StepsPanel";
import { ActivitiesPanel } from "../../components/studio/ActivitiesPanel";
import { ActivityConfigPanel } from "../../components/studio/ActivityConfigPanel";

type StudioProcessTemplateEditorPageProps = {
  templateId: string;
  userName?: string;
  onBack?: () => void;
};

export function StudioProcessTemplateEditorPage({
  templateId,
  userName = "Utilisateur",
  onBack,
}: StudioProcessTemplateEditorPageProps) {
  const [draft, setDraft] = useState<ProcessTemplateFullDetailStudio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);

  const name = draft?.name ?? "";
  const opsFamily = draft?.ops_family ?? "";
  const status = draft?.status ?? "—";
  const steps = draft?.current_version?.steps ?? [];
  const selectedStep = steps.find((s) => s.id === selectedStepId) ?? null;
  const activities = selectedStep?.activities ?? [];
  const selectedActivity = activities.find((a) => a.id === selectedActivityId) ?? null;

  const loadFull = useCallback(async (id: string) => {
    const orgId = getCurrentOrgId();
    if (!orgId) {
      setError("Aucune organisation sélectionnée.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const full = await getProcessTemplateFullStudio(id, orgId);
      setDraft(full);
      const firstStep = full.current_version?.steps?.[0];
      setSelectedStepId(firstStep?.id ?? null);
      setSelectedActivityId(firstStep?.activities?.[0]?.id ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (templateId === "new") {
      setLoading(true);
      setError(null);
      createProcessTemplateStudio({ name: "Nouveau template", ops_family: null })
        .then((created) => {
          window.location.hash = `#/studio/process-templates/${created.id}/editor`;
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "Erreur création");
          setLoading(false);
        });
      return;
    }
    loadFull(templateId);
  }, [templateId, loadFull]);

  const handleSave = async () => {
    if (!draft || templateId === "new") return;
    setSaving(true);
    try {
      const orgId = getCurrentOrgId();
      await updateProcessTemplateStudio(
        templateId,
        { name: name.trim() || undefined, ops_family: opsFamily.trim() || null },
        orgId
      );
      for (const step of draft.current_version?.steps ?? []) {
        await studioUpdateStep(
          templateId,
          step.id,
          {
            title: step.title,
            description: step.description ?? undefined,
            order_index: step.order_index,
            sla_days: step.sla_days ?? undefined,
          },
          orgId
        );
        for (const activity of step.activities) {
          await studioUpdateActivity(
            templateId,
            activity.id,
            {
              title: activity.title,
              description: activity.description ?? undefined,
              is_required: activity.is_required,
              sla_hours: activity.sla_hours ?? undefined,
              assignee_type: activity.assignee_type ?? undefined,
              assignee_role: activity.assignee_role ?? undefined,
              activity_ui_config: (activity.activity_ui_config ?? undefined) as Record<string, unknown> | undefined,
              checklist_config: activity.checklist_config ?? undefined,
              assignee_activity_config: activity.assignee_activity_config ?? undefined,
            },
            orgId
          );
        }
      }
      await loadFull(templateId);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!draft || templateId === "new") return;
    setPublishing(true);
    setPublishError(null);
    try {
      const orgId = getCurrentOrgId();
      await publishProcessTemplateStudio(templateId, {}, orgId);
      await loadFull(templateId);
    } catch (e: unknown) {
      setPublishError(e instanceof Error ? e.message : "Erreur publication");
    } finally {
      setPublishing(false);
    }
  };

  const setDraftSteps = (nextSteps: ProcessStepTemplateStudioOut[]) => {
    if (!draft?.current_version) return;
    setDraft({
      ...draft,
      current_version: {
        ...draft.current_version,
        steps: nextSteps,
      },
    });
  };

  const setDraftActivitiesForStep = (stepId: string, nextActivities: ProcessActivityTemplateStudioOut[]) => {
    if (!draft?.current_version) return;
    setDraft({
      ...draft,
      current_version: {
        ...draft.current_version,
        steps: draft.current_version.steps.map((s) =>
          s.id === stepId ? { ...s, activities: nextActivities } : s
        ),
      },
    });
  };

  if (loading && !draft) {
    return (
      <AppShell userName={userName} topbar={<span className="text-sm font-medium text-text">Editeur</span>}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted" />
        </div>
      </AppShell>
    );
  }

  if (error && !draft) {
    return (
      <AppShell userName={userName} topbar={<span className="text-sm font-medium text-text">Editeur</span>}>
        <div className="ds-container py-8">
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
            {onBack && (
              <button type="button" onClick={onBack} className="ml-2 underline">
                Retour
              </button>
            )}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4 flex-wrap">
          {onBack && (
            <button type="button" onClick={onBack} className="text-sm text-muted hover:text-text">
              Process Templates
            </button>
          )}
          <span className="text-sm font-medium text-text">Editeur</span>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <header className="border-b border-border bg-surface px-4 py-3 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Input
              label="Nom"
              value={name}
              onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value } : null))}
              placeholder="Nom du template"
              hideLabel
            />
            <Input
              label="Famille"
              value={opsFamily}
              onChange={(e) => setDraft((d) => (d ? { ...d, ops_family: e.target.value || null } : null))}
              placeholder="Famille"
              hideLabel
            />
            <span className="text-sm text-muted">Statut: {status}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button label="Enregistrer" intent="secondary" size="sm" onClick={handleSave} state={saving ? "loading" : "default"} />
            <Button label="Publier" intent="primary" size="sm" onClick={handlePublish} state={publishing ? "loading" : "default"} />
            <Button label="Versions" intent="ghost" size="sm" onClick={() => {}} />
          </div>
        </header>
        {publishError && (
          <div className="rounded border border-destructive/50 bg-destructive/5 mx-4 mt-2 px-3 py-2 text-sm text-destructive flex items-center gap-2">
            <span>{publishError}</span>
            <button type="button" onClick={() => setPublishError(null)} className="underline">Fermer</button>
          </div>
        )}

        <div className="flex flex-1 min-h-0">
          <div className="w-64 shrink-0 border-r border-border overflow-auto bg-muted/20">
            <StepsPanel
              steps={steps}
              selectedStepId={selectedStepId}
              onSelectStep={setSelectedStepId}
              onStepsChange={setDraftSteps}
              templateId={templateId}
            />
          </div>
          <div className="w-72 shrink-0 border-r border-border overflow-auto bg-muted/10">
            <ActivitiesPanel
              activities={activities}
              selectedActivityId={selectedActivityId}
              onSelectActivity={setSelectedActivityId}
              onActivitiesChange={(next) => selectedStepId && setDraftActivitiesForStep(selectedStepId, next)}
              stepId={selectedStepId}
              templateId={templateId}
            />
          </div>
          <div className="flex-1 overflow-auto p-4">
            <ActivityConfigPanel
              activity={selectedActivity}
              onActivityChange={(next) => {
                if (!selectedStepId || !next) return;
                const nextActivities = activities.map((a) => (a.id === next.id ? next : a));
                setDraftActivitiesForStep(selectedStepId, nextActivities);
              }}
              templateId={templateId}
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
